import React from 'react';
import { alpha } from '@mui/material/styles';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ReplyIcon from '@mui/icons-material/Reply';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import HowToVoteOutlinedIcon from '@mui/icons-material/HowToVoteOutlined';
import SportsKabaddiIcon from '@mui/icons-material/SportsKabaddi';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { RED, YELLOW, yellowText } from './brandColors';

export const extractUsername = (message) => {
  const match = message?.match(/@(\w+)/);
  return match ? match[1] : null;
};

// Strip internal metadata prefixes like [versus:CODE] from display messages
export const cleanMessage = (message) => {
  if (!message) return '';
  return message.replace(/\[(versus|agree|mindmeld|memory):[A-Z0-9]+\]\s*/, '');
};

export const getNotifMeta = (type, size = 16) => {
  switch (type) {
    case 'follow':
      return { icon: <PersonAddOutlinedIcon sx={{ fontSize: size }} />, color: '#1890FF', bg: alpha('#1890FF', 0.12) };
    case 'comment':
      return { icon: <ChatBubbleOutlineIcon sx={{ fontSize: size }} />, color: RED, bg: alpha(RED, 0.12) };
    case 'reply':
      return { icon: <ReplyIcon sx={{ fontSize: size }} />, color: '#9C27B0', bg: alpha('#9C27B0', 0.12) };
    case 'vote_milestone':
      // Yellow icons use the mode-aware yellowText callback (sx resolves it
      // with the theme) so they stay legible on light chip fills.
      return { icon: <EmojiEventsOutlinedIcon sx={{ fontSize: size }} />, color: yellowText, bg: alpha(YELLOW, 0.15) };
    case 'profile_view':
      return { icon: <VisibilityOutlinedIcon sx={{ fontSize: size }} />, color: '#6B7280', bg: alpha('#6B7280', 0.12) };
    case 'vote_received':
      return { icon: <HowToVoteOutlinedIcon sx={{ fontSize: size }} />, color: RED, bg: alpha(RED, 0.12) };
    case 'challenge_invite':
      return { icon: <SportsKabaddiIcon sx={{ fontSize: size }} />, color: '#D32F2F', bg: alpha('#D32F2F', 0.12) };
    case 'comment_like':
      return { icon: <FavoriteBorderIcon sx={{ fontSize: size }} />, color: RED, bg: alpha(RED, 0.12) };
    case 'match_joined':
      return { icon: <GroupAddOutlinedIcon sx={{ fontSize: size }} />, color: '#2E7D32', bg: alpha('#2E7D32', 0.12) };
    case 'match_submitted':
      return { icon: <SendOutlinedIcon sx={{ fontSize: size }} />, color: '#1976D2', bg: alpha('#1976D2', 0.12) };
    case 'match_completed':
      return { icon: <DoneAllIcon sx={{ fontSize: size }} />, color: yellowText, bg: alpha(YELLOW, 0.15) };
    case 'dm_message':
      // Mail, not a chat bubble: 'comment' already owns the chat bubble in
      // this list, and a DM row sitting beside a comment row must read as a
      // different kind of thing at a glance.
      return { icon: <MailOutlineIcon sx={{ fontSize: size }} />, color: RED, bg: alpha(RED, 0.12) };
    case 'question_removed':
      return { icon: <GavelOutlinedIcon sx={{ fontSize: size }} />, color: '#B71C1C', bg: alpha('#B71C1C', 0.12) };
    case 'mindmeld_started':
      return { icon: <PlayCircleOutlineIcon sx={{ fontSize: size }} />, color: '#2E7D32', bg: alpha('#2E7D32', 0.12) };
    case 'mindmeld_ended':
      return { icon: <StopCircleOutlinedIcon sx={{ fontSize: size }} />, color: '#6B7280', bg: alpha('#6B7280', 0.12) };
    case 'mindmeld_host_transferred':
      return { icon: <WorkspacePremiumOutlinedIcon sx={{ fontSize: size }} />, color: yellowText, bg: alpha(YELLOW, 0.15) };
    default:
      return { icon: <NotificationsNoneIcon sx={{ fontSize: size }} />, color: 'text.disabled', bg: 'action.hover' };
  }
};

export const getNotifDestination = (notif) => {
  // Any match/room notif embeds [mode:CODE] for deep-linking.
  if ([
    'challenge_invite',
    'match_joined',
    'match_submitted',
    'match_completed',
    'mindmeld_started',
    'mindmeld_ended',
    'mindmeld_host_transferred',
  ].includes(notif.type)) {
    const match = notif.message?.match(/\[(versus|agree|mindmeld|memory):([A-Z0-9]+)\]/);
    if (match) return `/play/${match[1]}/${match[2]}`;
  }
  // DM bells carry the conversation id in target_id (generic non-question
  // target). Checked before question_id: a DM bell must open the thread even
  // if a future notification type were to carry both.
  if (notif.type === 'dm_message' && notif.target_id) return `/messages/${notif.target_id}`;
  if (notif.question_id) return `/v/${notif.question_id}`;
  const username = notif.actor_username || extractUsername(notif.message);
  if ((notif.type === 'follow' || notif.type === 'profile_view') && username) {
    return `/u/${username}`;
  }
  return null;
};
